import { signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { html, list } from "../lib/bind";
import { labelRow } from "../lib/table";
import { formatMoney } from "../lib/money";
import { toast } from "../lib/toast";
import { buildBankingSubNav } from "./banking-subnav";
import {
  type ReconciliationSuggestion,
  acceptReconciliationSuggestion,
  listReconciliationSuggestions,
} from "../services/reconciliation";

export function bankingReconciliationView(): Node {
  const suggestions = signal<ReconciliationSuggestion[]>([]);

  function reloadSuggestions(): void {
    listReconciliationSuggestions()
      .then((result) => {
        suggestions.value = result;
      })
      .catch(() => undefined);
  }
  reloadSuggestions();

  async function acceptSuggestion(s: ReconciliationSuggestion): Promise<void> {
    try {
      await acceptReconciliationSuggestion(s.transaction_id, s.invoice_id);
      toast.success("Marked as paid.");
      reloadSuggestions();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Accept failed");
    }
  }

  const fragment = html`<div class="view">
    <div class="card">
      <div class="card-label">Reconciliation suggestions</div>
      <div class="data-table suggestions-table">
        <table>
          <thead>
            <tr>
              <th>Transaction</th>
              <th>Invoice</th>
              <th>Outstanding</th>
              <th>Score</th>
              <th>Reason</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="suggestions-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("banking reconciliation view produced no .view element");
  viewEl.insertBefore(buildBankingSubNav("reconciliation"), viewEl.firstChild);

  const suggestionsTable = fragment.querySelector<HTMLTableElement>(".suggestions-table table");
  const suggestionsBody = fragment.querySelector<HTMLTableSectionElement>(".suggestions-body");
  if (!suggestionsTable || !suggestionsBody) {
    throw new Error("banking reconciliation view markup missing expected elements");
  }

  list(
    suggestionsBody,
    suggestions,
    (s) => s.transaction_id,
    (s) => {
      const row = html`<tr>
        <td>${s.transaction_description} (${formatMoney(s.transaction_amount)}, ${s.transaction_date})</td>
        <td>${s.invoice_number ?? "—"} — ${s.client_name}</td>
        <td>${formatMoney(s.invoice_outstanding)}</td>
        <td>${String(s.score)}</td>
        <td>${s.reason}</td>
        <td><button class="btn btn-primary accept-btn" type="button">Accept</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("suggestion row template produced no element");
      const acceptBtn = row.querySelector<HTMLButtonElement>(".accept-btn");
      acceptBtn?.addEventListener("click", () => {
        void acceptSuggestion(s);
      });
      return labelRow(row, suggestionsTable);
    },
  );

  return fragment;
}
